import { InfoIcon } from '@phosphor-icons/react'
import { endOfDay } from 'date-fns'
import { useState } from 'react'
import DatePicker from 'react-datepicker'
import Select from 'react-select'

import {
  useDeleteSaltoSpaceFob,
  useGetSaltoSpaceFobs,
  useRevokeSaltoSpaceFob,
  useUpdateSaltoSpaceFob,
} from '@/api/keyless/salto-space/get-salto-space-fobs'
import { Tooltip } from '@/components/ui/tooltip'

import { getSelectedItemsValue, saltoSpaceDropdownStyles } from '../helpers'
import SaltoSpaceIssueFobModal from './SaltoSpaceIssueFobModal'
import ConfirmPopup from '../../../Popups/ConfirmPopup'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'

const EXTERNAL_FOB_TOOLTIP =
  'Physical fobs/cards created directly in Salto Space. You can update their access or revoke them here.'

const UPDATE_PREPARE_MESSAGE = (
  <>
    You are about to update a physical fob/card.
    <br />
    Place the resident's fob/card on the encoder now, and click Next to continue.
  </>
)

const REVOKE_PREPARE_MESSAGE = (
  <>
    You are about to revoke this physical fob/card from the resident.
    <br />
    Place the fob/card on the encoder now, and click Next to continue.
  </>
)

const DELETE_CONFIRM_MESSAGE =
  'Permanently removes this card from Salto Space. Use this only if the physical card is lost, stolen, or otherwise unrecoverable, unlike Revoke, a deleted card/FOB cannot be handed to another resident. This action cannot be undone. Are you sure you want to permanently delete this card?'

const sameIds = (a, b) => a.length === b.length && a.every((id) => b.includes(id))

export const ExternalFobRecord = ({
  fob,
  buildingId,
  email,
  saltoSpaceDoors,
  saltoSpaceGroups,
  saltoSpaceAccessLevels,
}) => {
  const [doorIds, setDoorIds] = useState(fob.doorIds)
  const [groupIds, setGroupIds] = useState(fob.permissionGroupIds)
  const [accessLevelIds, setAccessLevelIds] = useState(fob.accessLevelIds)
  const [expirationDate, setExpirationDate] = useState(
    fob.expirationDate ? new Date(fob.expirationDate).getTime() : null,
  )
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [showRevokeModal, setShowRevokeModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const { mutateAsync: updateFob } = useUpdateSaltoSpaceFob(buildingId, email)
  const { mutateAsync: revokeFob } = useRevokeSaltoSpaceFob(buildingId, email)
  const { mutateAsync: deleteFob } = useDeleteSaltoSpaceFob(buildingId, email)
  const { displaySnackbar } = useSnackbar()

  const initialExpDay = fob.expirationDate ? new Date(fob.expirationDate).toDateString() : null
  const currentExpDay = expirationDate ? new Date(expirationDate).toDateString() : null
  const hasChanges =
    !sameIds(doorIds, fob.doorIds) ||
    !sameIds(groupIds, fob.permissionGroupIds) ||
    !sameIds(accessLevelIds, fob.accessLevelIds) ||
    currentExpDay !== initialExpDay

  const handleUpdate = (encoderId) =>
    updateFob({
      accessLevelIds,
      doorIds,
      encoderId,
      expirationDate: expirationDate ? endOfDay(new Date(expirationDate)).toISOString() : undefined,
      fobHolderId: fob.fobHolderId,
      permissionGroupIds: groupIds,
    })

  const handleRevoke = (encoderId) => revokeFob({ encoderId, fobHolderId: fob.fobHolderId })

  const handleDelete = async () => {
    try {
      setIsDeleting(true)
      await deleteFob({ fobHolderId: fob.fobHolderId })
      displaySnackbar('FOB/Card deleted successfully')
    } catch {
      displaySnackbar('Failed to delete the fob/card. Please try again.', true)
    } finally {
      setIsDeleting(false)
      setShowDeleteModal(false)
    }
  }

  return (
    <>
      <div className="columns">
        <div className="description-column">
          <div className="dropdown-buttons-container">
            <div className="dropdown-buttons-container-row">
              <div className="dropdown-container">
                <div className="dropdown-header">Door</div>
                <Select
                  isClearable
                  isMulti
                  classNamePrefix="dropdown-button"
                  options={saltoSpaceDoors}
                  getOptionLabel={(option) => option.name.toString()}
                  getOptionValue={(option) => option.saltoId}
                  value={getSelectedItemsValue(doorIds, saltoSpaceDoors)}
                  styles={saltoSpaceDropdownStyles()}
                  onChange={(options) => setDoorIds(options ? options.map((o) => o.saltoId) : [])}
                  placeholder="Select Door..."
                  closeMenuOnSelect={false}
                />
              </div>
              <div className="dropdown-container">
                <div className="dropdown-header">Zone</div>
                <Select
                  isClearable
                  isMulti
                  classNamePrefix="select"
                  options={saltoSpaceGroups}
                  getOptionLabel={(option) => option.name.toString()}
                  getOptionValue={(option) => option.saltoId}
                  value={getSelectedItemsValue(groupIds, saltoSpaceGroups)}
                  styles={saltoSpaceDropdownStyles()}
                  onChange={(options) => setGroupIds(options ? options.map((o) => o.saltoId) : [])}
                  placeholder="Select Zone..."
                  closeMenuOnSelect={false}
                />
              </div>
            </div>
            <div className="dropdown-buttons-container-row">
              <div className="dropdown-container">
                <div className="dropdown-header">Access Level</div>
                <Select
                  isClearable
                  isMulti
                  classNamePrefix="select"
                  options={saltoSpaceAccessLevels}
                  getOptionLabel={(option) => option.name.toString()}
                  getOptionValue={(option) => option.saltoId}
                  value={getSelectedItemsValue(accessLevelIds, saltoSpaceAccessLevels)}
                  styles={saltoSpaceDropdownStyles()}
                  onChange={(options) =>
                    setAccessLevelIds(options ? options.map((o) => o.saltoId) : [])
                  }
                  placeholder="Select Access Level..."
                  closeMenuOnSelect={false}
                />
              </div>
              <div className="dropdown-container">
                <div className="dropdown-header">Expiration Date</div>
                <DatePicker
                  className="dropdown-button"
                  placeholderText="MM/DD/YYYY"
                  selected={expirationDate || null}
                  onChange={(date) => setExpirationDate(new Date(date).getTime() || null)}
                  minDate={new Date()}
                  isClearable
                />
              </div>
            </div>
          </div>
          <button
            type="button"
            className="confirm-button button-primary button"
            disabled={!hasChanges}
            onClick={() => setShowUpdateModal(true)}
          >
            Update Fob/Card
          </button>
        </div>
      </div>
      <div className="columns">
        <div className="column column-right">
          <span className="salto-space-fob-date">
            {fob.fobHolderName ? `${fob.fobHolderName} · ` : ''}
            {fob.fobId ? `ROM ${fob.fobId}` : ''}
          </span>
          <button
            type="button"
            className="button button-bordered auto-width"
            onClick={() => setShowDeleteModal(true)}
          >
            Delete Fob/Card
          </button>
          <button
            type="button"
            className="button button-danger auto-width"
            onClick={() => setShowRevokeModal(true)}
          >
            Revoke Fob/Card
          </button>
        </div>
      </div>
      <SaltoSpaceIssueFobModal
        isOpen={showUpdateModal}
        buildingId={buildingId}
        headerTitle="Update physical fob/card"
        prepareMessage={UPDATE_PREPARE_MESSAGE}
        successTitle="Fob/card successfully updated!"
        successBody="The resident can now use the updated access configuration."
        onProgram={handleUpdate}
        onClose={() => setShowUpdateModal(false)}
        onSuccess={() => displaySnackbar('FOB/Card updated successfully')}
      />
      <SaltoSpaceIssueFobModal
        isOpen={showRevokeModal}
        buildingId={buildingId}
        headerTitle="Revoke physical fob/card"
        prepareMessage={REVOKE_PREPARE_MESSAGE}
        successTitle="Fob/card successfully revoked!"
        successBody="The resident can no longer use it for access."
        onProgram={handleRevoke}
        onClose={() => setShowRevokeModal(false)}
        onSuccess={() => {
          setShowRevokeModal(false)
          displaySnackbar('FOB/Card revoked successfully')
        }}
      />
      <ConfirmPopup
        title="Delete the FOB/Card?"
        body={DELETE_CONFIRM_MESSAGE}
        cancelText="Cancel"
        confirmText="Yes, delete"
        isOpen={showDeleteModal}
        cancelCallback={() => setShowDeleteModal(false)}
        confirmCallback={handleDelete}
        className="is-caution"
        isLoading={isDeleting}
      />
    </>
  )
}

const SaltoSpaceExternalFobs = ({
  isAvailable,
  buildingId,
  email,
  saltoSpaceDoors,
  saltoSpaceGroups,
  saltoSpaceAccessLevels,
}) => {
  const { data: fobs = [] } = useGetSaltoSpaceFobs(buildingId, email, isAvailable)
  const externalFobs = fobs.filter((fob) => fob.fobMarker === 'external' && fob.fobId)

  if (!isAvailable || externalFobs.length === 0) return null

  return (
    <>
      <div className="columns">
        <div className="description-column">
          <div className="element-name element-margin-top salto-space-fob-title">
            External Salto Space physical Fob/Card
            <Tooltip triggerIcon={InfoIcon}>{EXTERNAL_FOB_TOOLTIP}</Tooltip>
          </div>
        </div>
      </div>
      {externalFobs.map((fob) => (
        <ExternalFobRecord
          key={fob.fobHolderId}
          fob={fob}
          buildingId={buildingId}
          email={email}
          saltoSpaceDoors={saltoSpaceDoors}
          saltoSpaceGroups={saltoSpaceGroups}
          saltoSpaceAccessLevels={saltoSpaceAccessLevels}
        />
      ))}
    </>
  )
}

export default SaltoSpaceExternalFobs
